import { css } from "styled-components";

import { BaseButton } from "components/base";

/**
 * What the Jump to Location forms are drawn in: a column that holds the form's fields
 * and then one action row under them. The tabs read as one panel only while they
 * share this, so it lives here rather than being copied into each form.
 */
export const jumpPanelStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* The label's type, kept here rather than in the rule that draws it, because the space
     one label line takes is also what a form has to make room for above its first row.
     Both are read from these, so the two cannot drift apart. */
  --field-label-font-size: 1.2rem;
  --field-label-line-height: 1.2;
  --field-label-line: calc(var(--field-label-font-size) * var(--field-label-line-height));
  .flex-row {
    gap: 8px;
    div {
      flex-shrink: 0;
    }
  }
  .button {
    width: 45px;
    height: 40px;
    border-radius: 4px;
    color: var(--color-white);
  }
  /* The name of a control, drawn above it in normal flow and sitting straight on it,
     the way the bin settings draw theirs. It appears only once the control holds a
     value, because an empty one already names itself in its placeholder and would
     otherwise say the same word twice. The line it stands on is kept either way, so the
     rows under it do not step down as the form is filled in. Nothing is reserved with a
     margin, or the space would be there twice over: the line is set to the height of its
     own text so a row costs the label and nothing more. */
  .field-label {
    display: block;
    margin: 0;
    color: #9b9b9b;
    font-size: var(--field-label-font-size);
    line-height: var(--field-label-line-height);
    visibility: hidden;
  }
  .filled > .field-label {
    visibility: visible;
  }
  .action-row {
    /* Every button in this panel is given a filled 45px box with white text, which is
       what the Go button wants and the opposite of what this one does. So the fill, the
       fixed width and the white are all taken back here, leaving a text link that is
       only as wide as its own label. */
    .clear-all-button {
      width: auto;
      min-width: 0;
      height: auto;
      padding: 0;
      background: transparent;
      color: #666;
      font-size: 12px;
      text-decoration: underline;
      /* A long label is left at its full width rather than squeezed into the space Go
         leaves over. */
      flex-shrink: 0;

      &:hover,
      &:focus-visible {
        color: var(--color-primary-hover);
        background: transparent;
      }
      /* The panel clears every outline, so focus needs one drawn back. */
      &:focus-visible {
        outline: 1px dotted var(--color-primary-hover);
        outline-offset: 2px;
      }
    }
  }
`;

interface JumpActionRowProps {
  /** Names this row's two buttons: `${idPrefix}-clear-all` and `${idPrefix}-go`. */
  idPrefix: string;
  canSubmit: boolean;
  onClear: () => void;
  onSubmit: () => void;
}

/**
 * The row under every Jump to Location form: clear what is filled in, or go to it.
 *
 * Clear all stays put with nothing to clear rather than coming and going, because a
 * control that appears only sometimes is harder to find than one that does nothing.
 */
export function JumpActionRow({
  idPrefix,
  canSubmit,
  onClear,
  onSubmit
}: JumpActionRowProps) {
  return (
    <div className="flex-row justify-between items-center action-row">
      <BaseButton
        appearance="subtle"
        ellipses={false}
        className="clear-all-button"
        data-testid={`${idPrefix}-clear-all`}
        onClick={onClear}>
        Clear all
      </BaseButton>
      <BaseButton
        appearance="default"
        ellipses={false}
        data-testid={`${idPrefix}-go`}
        onClick={onSubmit}
        isDisabled={!canSubmit}>
        Go
      </BaseButton>
    </div>
  );
}
